import { useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Gauge, RotateCcw } from "lucide-react";
import { useCatalog } from "../../lib/useCatalog";
import { useHardware } from "../../context/HardwareContext";
import { evaluate, recommendedQuant } from "../../lib/compute";
import { VerdictBadge } from "../VerdictBadge";
import { CmdLine } from "../InstallCommands";

const USES = [
  ["chat", "Discuter / rédiger"],
  ["code", "Programmer"],
  ["reasoning", "Raisonnement / maths"],
  ["vision", "Analyser des images"],
  ["multilingual", "Plusieurs langues"],
  ["mobile", "Sur iPhone / mobile"],
];
const PRIORITIES = [
  ["balance", "Équilibre"],
  ["speed", "Vitesse"],
  ["quality", "Qualité maximale"],
];
const RANK = { can_run: 2, tight: 1, no_go: 0 };

function rank(models, use, priority, hw, quants) {
  return models
    .filter((m) => m.use_cases.includes(use))
    .map((m) => {
      const q = recommendedQuant(m, hw, quants);
      return { m, q, ev: evaluate(m, q, hw) };
    })
    .filter((r) => r.ev.verdict !== "no_go")
    .sort((a, b) => {
      if (priority !== "quality" && RANK[b.ev.verdict] !== RANK[a.ev.verdict]) return RANK[b.ev.verdict] - RANK[a.ev.verdict];
      if (priority === "speed") return b.ev.tokPerSec - a.ev.tokPerSec;
      return b.m.params - a.m.params;
    })
    .slice(0, 3);
}

function Choice({ options, value, onPick, prefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([v, l]) => (
        <button key={v} onClick={() => onPick(v)} data-testid={`${prefix}-${v}`}
          className={`rounded-lg border px-3 py-2 text-sm transition-colors ${value === v ? "border-primary bg-primary/10 text-primary" : "border-[#1E2638] bg-[#080B10] text-slate-300 hover:border-primary/60"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

export function GuidedAdvisor() {
  const { data } = useCatalog();
  const { hardware } = useHardware();
  const [use, setUse] = useState(null);
  const [priority, setPriority] = useState(null);
  const results = data && use && priority ? rank(data.models, use, priority, hardware, data.quantizations) : null;

  return (
    <div className="space-y-5 rounded-xl border border-[#1E2638] bg-[#0F141C] p-5" data-testid="guided-advisor">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium"><ClipboardList className="h-4 w-4 text-primary" /> Questionnaire guidé · sans IA, instantané</div>
        {use && (
          <button onClick={() => { setUse(null); setPriority(null); }} data-testid="guided-reset" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
            <RotateCcw className="h-3.5 w-3.5" /> Recommencer
          </button>
        )}
      </div>
      <p className="font-mono2 text-xs text-muted-foreground">
        Basé sur votre matériel : {hardware.ram} Go RAM · {hardware.vram} Go VRAM · {hardware.accelerator} — <Link to="/" className="text-primary hover:underline">modifier dans le Diagnostic</Link>
      </p>
      <div className="space-y-2">
        <div className="text-sm text-slate-200">1. Que voulez-vous faire ?</div>
        <Choice options={USES} value={use} onPick={setUse} prefix="guided-use" />
      </div>
      {use && (
        <div className="space-y-2">
          <div className="text-sm text-slate-200">2. Qu'est-ce qui compte le plus ?</div>
          <Choice options={PRIORITIES} value={priority} onPick={setPriority} prefix="guided-priority" />
        </div>
      )}
      {results && (
        <div className="space-y-3" data-testid="guided-results">
          <div className="text-sm text-slate-200">3. Nos recommandations</div>
          {results.length === 0 ? (
            <p data-testid="guided-no-result" className="text-sm text-amber-300">Aucun modèle de ce type ne tient dans votre matériel. Essayez un autre usage ou une machine plus puissante.</p>
          ) : results.map(({ m, q, ev }, i) => (
            <div key={m.id} data-testid="guided-result-card" className="space-y-2 rounded-lg border border-[#1E2638] bg-[#080B10] p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-head font-bold">{i + 1}. {m.name} <span className="font-mono2 text-xs font-normal text-muted-foreground">· {q.label} · ~{ev.requiredGB.toFixed(1)} Go</span></div>
                <VerdictBadge verdict={ev.verdict} />
              </div>
              <p className="text-xs text-muted-foreground">{m.desc} <Gauge className="inline h-3 w-3" /> ~{ev.tokPerSec} tok/s · {ev.mode}</p>
              <CmdLine cmd={`ollama run ${m.ollama}`} />
              <Link to={`/installation?model=${m.id}`} data-testid="guided-install-link" className="text-xs text-primary hover:underline">Guide d'installation complet →</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
